/**
 * Versioned plot preference model used by the scatterplot and timeseries widgets.
 * 
 * @packageDocumentation
 * @module PlotTypes
 * @public
 */

// -- type definitions --

export type CurveId = string;
export type AxisKey = string;
export type SourceSlotIndex = number;
/** How timeseries x-axis tick labels expose UTC offsets around DST ambiguity. */
export type TimeseriesAxisLabelMode = 'conditional' | 'global';

/** Default timeseries x-axis label mode when omitted from preferences. */
export const DEFAULT_TIMESERIES_AXIS_LABEL_MODE: TimeseriesAxisLabelMode =
  'conditional';

export type RefreshIntervalUnit = 'second' | 'minute' | 'hour' | 'day';
export type TimeRangeUnit =
  | 'minute'
  | 'hour'
  | 'day'
  | 'week'
  | 'month'
  | 'year';

export interface RefreshIntervalOption {
  quantity: number;
  unit: RefreshIntervalUnit;
}

export interface TimeRangeOption {
  quantity: number;
  unit: TimeRangeUnit;
}

/** Semantic role of a curve for interval merge and forecast invalidation (future). */
export type CurveDataRole = 'measured' | 'predicted' | 'derived';

export interface CurvePreferences {
  displayName?: string;
  color?: string;
  lineStyle?: 'solid' | 'dashed' | 'dotted';
  thicknessPx?: number;
  visible?: boolean;
  shape?: 'spline' | 'linear' | 'vhv' | 'hvh' | 'vh' | 'hv';
  /** Defaults to measured when omitted. Merge rules using roles are deferred. */
  dataRole?: CurveDataRole;
}

export interface AxisPreferences {
  displayName?: string;
  min?: number;
  max?: number;
  /** Whether min/max are active. */
  useBounds?: boolean;
  /** Whether pan/zoom in the Y axis is enabled. */
  zoomEnabled?: boolean;
  /** @deprecated Use useBounds and zoomEnabled; upgraded on load via upgradePlotPreferences. */
  locked?: boolean;
  visible?: boolean;
  isXAxis?: boolean;
}

export type CurveRelationType = 'interval' | 'correlation' | 'difference';

export interface CurveRelation {
  relationType: CurveRelationType;
  mainCurve: CurveId;
  relatedCurves: CurveId[];
  displayName?: string;
  color?: string;
  visible?: boolean;
}

export interface ThresholdCatalogSnapshotVariable {
  variableName: string;
  dataType:
    | 'number'
    | 'datetime'
    | 'category'
    | 'vector'
    | 'boolean'
    | 'string';
  role: 'independent' | 'dependent' | 'auxiliary' | 'time';
  dimensionRef: string;
  required: boolean;
}

export interface ThresholdCatalogSnapshotStyle {
  color: string | null;
  dash: 'solid' | 'dash' | 'dot' | 'longdash' | 'dashdot' | null;
  width: number | null;
  fillColor: string | null;
  opacity: number | null;
  rule: Record<string, unknown> | null;
}

export interface ThresholdCatalogSnapshotAvailableStyle
  extends ThresholdCatalogSnapshotStyle {
  id: number;
  label: string;
  isDefault: boolean;
}

export interface ThresholdCatalogSnapshotSourceBinding {
  measure_point_id: number;
  measure_type_id: number;
}

export interface ThresholdCatalogSnapshot {
  id: number;
  thresholdId: number;
  label: string;
  description: string;
  boundKind: 'maximum' | 'minimum';
  typeLabel: string;
  type:
    | 'constant'
    | 'affine'
    | 'polynomial'
    | 'piecewise'
    | 'circle'
    | 'rectangle'
    | 'mesh'
    | 'custom';
  variables: string[];
  variableDetails: ThresholdCatalogSnapshotVariable[];
  parameterSummary: string;
  rawParameters: Record<string, unknown>;
  isLegacy: boolean;
  evaluationCoordinateSystem: string;
  representationCoordinateSystem: string;
  representationMode: string;
  activeFrom: string | null;
  activeTo: string | null;
  defaultStyle: ThresholdCatalogSnapshotStyle | null;
  availableStyles: ThresholdCatalogSnapshotAvailableStyle[];
  sourceBindings: ThresholdCatalogSnapshotSourceBinding[];
}

export type ThresholdStyleSelectionPreference =
  | { mode: 'default' }
  | { mode: 'explicit-style-id'; thresholdAssignmentStyleId: number };

export interface ThresholdRequestContextPrefs {
  selectedThresholdAssignmentIds?: number[];
  selectedThresholdCatalog?: ThresholdCatalogSnapshot[];
  variableMappingsByThresholdId?: Record<number, Record<string, string>>;
}

export interface ThresholdVisualizationPrefs {
  enabled: boolean;
  markerBorderWidthPx?: number;
  markerBorderOpacity?: number;
  styleSelectionByThresholdId?: Record<
    number,
    ThresholdStyleSelectionPreference
  >;
  lineShapeByThresholdAssignmentId?: Record<
    number,
    'linear' | 'vhv' | 'hvh' | 'vh' | 'hv'
  >;
  targetCurveIdsByThresholdAssignmentId?: Record<number, string[]>;
}

export interface ThresholdPrefs {
  request?: ThresholdRequestContextPrefs;
  visualization?: ThresholdVisualizationPrefs;
}

export interface ToolBarPreferencesV1 {
  hide: boolean;
  useTimeRangePills: boolean;
  useReloadButton: boolean;
  useRefreshIntervalSelector: boolean;
}

export interface ToolBarPreferencesV2 extends ToolBarPreferencesV1 {
  selectedRefreshInterval?: RefreshIntervalOption;
  refreshIntervalOptions: RefreshIntervalOption[];
  timeRangeOptions: TimeRangeOption[];
}

export type ToolBarPreferences = ToolBarPreferencesV1 | ToolBarPreferencesV2;

interface PlotPreferencesBase {
  curvePrefs: Record<CurveId, CurvePreferences>;
  curveOrder?: CurveId[];
  curveToSourceIndex?: Record<CurveId, SourceSlotIndex>;
  /** IANA timezone per curve; defaults to `UTC` when a source omits timezone. */
  curveToTimezone?: Record<CurveId, string>;
  /** Timeseries x-axis tick label mode; defaults to {@link DEFAULT_TIMESERIES_AXIS_LABEL_MODE}. */
  timeseriesAxisLabelMode?: TimeseriesAxisLabelMode;
  axisPrefs: Record<AxisKey, AxisPreferences>;
  dataSourceToAxis: Record<CurveId, AxisKey>;
  yAxisOrder: AxisKey[];
  showRangeSlider: boolean;
  curveRelations?: CurveRelation[];
  thresholdPrefs?: ThresholdPrefs;
}

export interface PlotPreferencesV1 extends PlotPreferencesBase {
  version?: 1;
  toolBarPrefs: ToolBarPreferencesV1;
}

export interface PlotPreferencesV2 extends PlotPreferencesBase {
  version: 2;
  toolBarPrefs: ToolBarPreferencesV2;
}

export interface PlotPreferencesV3 extends PlotPreferencesBase {
  version: 3;
  toolBarPrefs: ToolBarPreferencesV2;
}

export type PlotPreferences =
  | PlotPreferencesV1
  | PlotPreferencesV2
  | PlotPreferencesV3;
